import { DateService, DateUnit } from "@app/services/date.service";
import { VideoCoachingStatus } from "@components/videos/enums/VideoCoachingStatus.enum";
import { VideoReviewStatus } from "@components/videos/enums/VideoReviewStatus.enum";
import { VideoReviewSubStatus } from "@components/videos/enums/VideoReviewSubStatus.enum";
import { IDriver } from "@interfaces/IDriver";
import { IDriverFilter } from "@interfaces/filters/IDriverFilter";
import { ChartService } from "@services/charts.service";
import { JourneyService } from "@services/journey.service";
import { ObservationsService } from "@services/observations.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { VeriDate } from "@services/veriDate.service";
import { CameraDisplayService } from "@videos/cameraDisplay.service";
import * as angular from "angular";
import * as moment from "moment";
import { VideoObservation } from "../enums/VideoObservation";
import { VideoStatus } from "../enums/VideoStatus";
import { IVideoEntry } from "../models/IVideoEntry";
import { VideoEntryService } from "../services/video-entry.service";
import { VideoEventCodeService } from "../services/video-event-code.service";
import "./driverOverviewPage.scss";

interface BarSeriesConfig {
    name: string;
    y: number;
    color: string
}

class ComponentController implements angular.IComponentController {

    loading = false;

    driver: IDriver;
    videoEvents: IVideoEntry[];

    needsReviewCount: number = 0;
    noActionRequiredCount: number = 0;
    actionRequiredCount: number = 0;
    driverTrainedCount: number = 0;
    driverPraisedCount: number = 0;
    totalVideoEvents: number = 0;
    totalAverageDriverVideoEvents: number = 0;
    totalNewVideos: number = 0;

    totalDistance: string;

    pieChartConfig: Highcharts.Options;
    barChartConfig: Highcharts.Options;

    categories: string[] = [];
    currentDriverSeries: BarSeriesConfig[] = [];
    averageDriverSeries: BarSeriesConfig[] = [];

    latestVideos: IVideoEntry[] = [];

    onDashboardBoxClick: (args: { tab: string, status: string }) => void;

    static $inject = ["$window", "cameraDisplayService", "observationsService", "chartService", "veriDate", "journeyService", "unitConversionService", "translationService", "videoEventCodeService", "videoEntryService", "dateService"];

    constructor(
        private readonly $window: angular.IWindowService,
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly observationsService: ObservationsService,
        private readonly chartService: ChartService,
        private readonly veriDate: VeriDate,
        private readonly journeyService: JourneyService,
        private readonly unitConversionService: UnitConversionService,
        private readonly translationService: TranslationService,
        private readonly videoEventCodeService: VideoEventCodeService,
        private readonly videoEntryService: VideoEntryService,
        private readonly dateService: DateService) {
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {
        this.setTotalDistance();
        this.getVideoData();
    }

    setSelectedTab(tab: string, status: string = null) {
        if (this.onDashboardBoxClick) {
            this.onDashboardBoxClick({ tab: tab, status: status });
        }
    }

    playVideo(event: { videoEntry: IVideoEntry, $event: MouseEvent }) {
        this.cameraDisplayService.showVideoPlayer(event.videoEntry, event.$event);
    }

    // ---

    private setTotalDistance() {
        this.journeyService.getJourneysForDriverSlim(this.driver.Id, moment().subtract(14, 'days'), moment())
            .then(journeys => {
                let distance = 0;

                journeys.forEach(journey => {
                    distance += journey.DistanceKm;
                });

                this.totalDistance = this.unitConversionService.smartConvert(distance, UnitConversionType.Distance, { scaleLargeNumbers: true });
            });
    }

    private setDisplay() {
        this.createChartConfigs();
        this.getStatusCounts(this.driver?.videoEvents);
        this.getAverageDriverVideoCount(this.videoEvents);
    }

    private getVideoData() {
        const driverFilter: IDriverFilter = {
            group: null,
            driver: this.driver
        }

        const now = new Date();
        const start = this.dateService.addOrSubtractTime(now, -7, DateUnit.Day);
        const end = now;

        this.videoEntryService.getVideosEntriesWithMappings(start, end, { processEntries: true, downloadedOnly: true, driverFilter: driverFilter, invalidateCache: true })
            .then(videos => {
                this.driver.videoEvents = videos.filter(v => v.DriverId === this.driver.Id);
                this.getNewVideoCount(videos);

                videos.sort((a, b) => {
                    return this.veriDate.sort(a.DateCreated, b.DateCreated);
                });

                if (this.$window.outerWidth >= 1440) {
                    this.latestVideos = videos.slice(0, 4);
                } else {
                    this.latestVideos = videos.slice(0, 2);

                }
            }).finally(() => {
                this.setDisplay();
            });
    }

    private getNewVideoCount(videos: IVideoEntry[]) {
        videos.forEach(video => {
            if (!video.watched) {
                this.totalNewVideos++;
            }
        });
    }

    private createChartConfigs() {
        this.pieChartConfig = this.chartService.createChartOptions("pie");
        this.barChartConfig = this.chartService.createChartOptions("column");

        var legandOptions = {
            enabled: true,
            verticalAlign: 'bottom',
            itemMarginTop: 5
        }

        this.pieChartConfig.plotOptions.pie.size = '80%';

        this.chartService.setLegendOptions(this.pieChartConfig, legandOptions);

        this.createChartSeries();
    }

    private createChartSeries() {
        this.createPieChartSeries();
        this.createBarChartSeries();
    }

    private createPieChartSeries() {
        const series = [{
            name: this.translationService.t("driverOverviewPage.count"),
            colorByPoint: true,
            data: []
        }];

        const events = this.filterMinorEvents(this.driver?.videoEvents);

        events.forEach(videoEvent => {
            videoEvent?.MetaData?.VideoObservations?.forEach(observation => {
                const foundObservation = series[0].data.find(s => s.name == VideoObservation[observation]);

                if (foundObservation) {
                    foundObservation.y++;
                }
                else {

                    series[0].data.push({
                        name: this.translationService.translateEnum(VideoObservation, "VideoObservation", observation),
                        y: 1,
                        color: this.observationsService.getObservationsColour(observation)
                    });
                }
            });
        });

        this.pieChartConfig.series = series;
    }

    private createBarChartSeries() {
        let allEvents = this.filterMinorEvents(this.videoEvents);

        this.createCategoriesSetCurrentDriverSeries();
        this.setAverageDriverSeries(allEvents);

        // get unique driver count for finding average
        const uniqueDriverCount = this.getUniqueDriverCount(allEvents);

        //divide the total count of each data by unique drivers
        this.averageDriverSeries.forEach(data => {
            const updatedCount = (data.y / uniqueDriverCount);
            data.y = Number(updatedCount.toFixed(2));
        });

        this.barChartConfig.series = [
            { name: `${this.driver.DisplayName}`, data: this.currentDriverSeries },
            { name: this.translationService.t(`driverOverviewPage.averageDriver`), data: this.averageDriverSeries }
        ];

        this.barChartConfig.xAxis = {
            categories: this.categories
        };
    }

    private createCategoriesSetCurrentDriverSeries() {
        const driverEvents = this.filterMinorEvents(this.driver.videoEvents);

        driverEvents?.forEach(event => {
            event?.MetaData?.VideoObservations?.forEach(observation => {
                // set categories
                if (!this.categories.find(cat => cat == VideoObservation[observation])) {
                    this.categories.push(this.translationService.translateEnum(VideoObservation, "VideoObservation", observation));
                }

                const existingDriverData = this.currentDriverSeries.find(cat => cat.name == VideoObservation[observation]);

                // if cant find then add to currentDriver and also add to averageDriverSeries so data matches
                if (!existingDriverData) {
                    this.currentDriverSeries.push({ name: this.translationService.translateEnum(VideoObservation, "VideoObservation", observation), y: 1, color: this.observationsService.getObservationsColour(observation) });
                    this.averageDriverSeries.push({ name: this.translationService.translateEnum(VideoObservation, "VideoObservation", observation), y: 0, color: 'rgba(0, 0, 0, 0.2)' });
                }
                else {
                    existingDriverData.y++;
                }
            });
        });
    }

    private setAverageDriverSeries(events: IVideoEntry[]) {

        events?.forEach(event => {
            event?.MetaData?.VideoObservations?.forEach(observation => {
                const existingDriverData = this.averageDriverSeries.find(data => data.name == this.translationService.translateEnum(VideoObservation, "VideoObservation", observation));

                if (existingDriverData) {
                    existingDriverData.y++;
                }
            });
        });
    }

    private getUniqueDriverCount(videoEntries: IVideoEntry[]): number {
        let uniqueDrivers = [];

        videoEntries.forEach(entry => {
            if (entry?.DriverId && !uniqueDrivers.find(ud => ud == entry.DriverId)) {
                uniqueDrivers.push(entry.DriverId);
            }
        });

        return uniqueDrivers.length || 0;
    }


    private filterMinorEvents(events: IVideoEntry[]): IVideoEntry[] {
        events = events.filter(e => !this.videoEventCodeService.isMinorEvent(e));

        return events;
    }

    private getAverageDriverVideoCount(videoEvents: IVideoEntry[]) {
        const uniqueDriverCount = this.getUniqueDriverCount(videoEvents);

        this.totalAverageDriverVideoEvents = Math.round(videoEvents.length / uniqueDriverCount);
    }

    private getStatusCounts(events: IVideoEntry[]) {
        this.resetStatusCounts();
        events = this.filterMinorEvents(events);

        this.totalVideoEvents = events.length;

        events.forEach(event => {
            if (event.Status !== VideoStatus.Downloaded) {
                return;
            }

            switch (event.reviewStatus) {
                case VideoReviewStatus.NeedsReview:
                    this.needsReviewCount++;
                    break;

                case VideoReviewStatus.Reviewed:
                    if (event.reviewSubStatus == VideoReviewSubStatus.ActionRequired) {
                        this.actionRequiredCount++;
                    }

                    if (event.reviewSubStatus == VideoReviewSubStatus.NoActionRequired) {
                        this.noActionRequiredCount++;
                    }
                    break;

                case VideoReviewStatus.Coached:
                    if (event.coachingStatus == VideoCoachingStatus.DriverTrained) {
                        this.driverTrainedCount++;
                    }

                    if (event.coachingStatus == VideoCoachingStatus.Praise) {
                        this.driverPraisedCount++;
                    }
                    break;
            }
        });
    }

    private resetStatusCounts() {
        this.needsReviewCount = 0;
        this.actionRequiredCount = 0;
        this.noActionRequiredCount = 0;
        this.driverTrainedCount = 0;
        this.driverPraisedCount = 0;
        this.totalNewVideos = 0;
        this.totalAverageDriverVideoEvents = 0;
    }
}

export class DriverOverviewPageComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            driver: "<",
            videoEvents: "<",
            onDashboardBoxClick: "&"
        };

        this.controller = ComponentController;
        this.template = require("./driverOverviewPage.html");
    }
}
